import { zodResolver } from '@hookform/resolvers/zod';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Check, Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import i18n from '@/Config/app/i18n';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { DialogFooter, DialogHeader, DialogTitle } from '@/Domain/components/Shared/Dialog/Dialog';
import { FormItem, FormLabel, FormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';

const MAX_DESCRIPTION_LENGTH = 500;

const SCOPE_READ_ONLY = ['read_all'];
const SCOPE_READ_WRITE = ['read_all', 'write_all'];

const createTokenSchema = (existingDescriptions: string[]) =>
  z.object({
    description: z
      .string()
      .min(
        1,
        i18n.t('integrations:api_integration.sheet.create_dialog.confirm.provide_description'),
      )
      .max(
        MAX_DESCRIPTION_LENGTH,
        i18n.t('integrations:api_integration.sheet.create_dialog.field.max_length', {
          max: MAX_DESCRIPTION_LENGTH,
        }),
      )
      .refine(
        (val) => !existingDescriptions.map((d) => d.trim()).includes(val.trim()),
        i18n.t(
          'integrations:api_integration.sheet.create_dialog.confirm.description_must_be_unique',
        ),
      ),
    scopeOption: z.enum(['read_only', 'read_write']),
  });

type CreateTokenForm = z.infer<ReturnType<typeof createTokenSchema>>;

interface CreateTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isCreating: boolean;
  onSubmit: (description: string, scopes: string[]) => void;
  existingDescriptions: string[];
}

export const CreateTokenDialog = ({
  open,
  onOpenChange,
  isCreating,
  onSubmit,
  existingDescriptions,
}: CreateTokenDialogProps) => {
  const { t } = useTranslation(['integrations']);

  const existingDescriptionsRef = useRef<string[]>([]);
  existingDescriptionsRef.current = existingDescriptions;

  const form = useForm<CreateTokenForm>({
    mode: 'onChange',
    defaultValues: {
      description: '',
      scopeOption: 'read_only',
    },
    resolver: (data, context, options) => {
      const schema = createTokenSchema(existingDescriptionsRef.current);
      return zodResolver(schema)(data, context, options);
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    trigger,
    formState: { errors, isValid },
  } = form;

  const description = watch('description');

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (open && description) {
      trigger('description');
    }
  }, [existingDescriptions, open, description, trigger]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 z-20" />

      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
        data-testid="create-token-dialog"
      >
        <div className="bg-card rounded-lg p-6 max-w-2xl w-full shadow-lg">
          <form
            onSubmit={handleSubmit((data) =>
              onSubmit(
                data.description,
                data.scopeOption === 'read_only' ? SCOPE_READ_ONLY : SCOPE_READ_WRITE,
              ),
            )}
          >
            <DialogHeader>
              <DialogTitle data-testid="create-token-heading">
                {t('integrations:api_integration.sheet.create_dialog.title')}
              </DialogTitle>
              <p className="text-sm text-foreground-subtle">
                {t('integrations:api_integration.sheet.create_dialog.description')}
              </p>
            </DialogHeader>

            <FormItem className="py-4">
              <FormLabel htmlFor="token-description" error={errors.description}>
                {t('integrations:api_integration.sheet.create_dialog.field.description')}
              </FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <AutosizeTextarea
                    id="token-description"
                    data-testid="token-description-input"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={t(
                      'integrations:api_integration.sheet.create_dialog.field.placeholder',
                    )}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    minHeight={100}
                    autoFocus
                  />
                )}
              />
              <div className="flex justify-between items-start">
                <FormMessage error={errors.description} data-testid="description-error" />
                <p className="text-xs text-foreground-subtle" data-testid="character-count">
                  {description.length}/{MAX_DESCRIPTION_LENGTH}{' '}
                  {t('integrations:api_integration.sheet.create_dialog.field.characters')}
                </p>
              </div>
            </FormItem>

            <FormItem className="pb-4">
              <FormLabel>
                {t('integrations:api_integration.sheet.create_dialog.field.permissions')}
              </FormLabel>
              <Controller
                name="scopeOption"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-3" data-testid="scope-options">
                    <label
                      className={`flex-1 cursor-pointer rounded-lg border p-3 text-sm transition-colors ${
                        field.value === 'read_only'
                          ? 'border-foreground bg-muted'
                          : 'border-border hover:border-border'
                      }`}
                      data-testid="scope-option-read-only"
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        value="read_only"
                        checked={field.value === 'read_only'}
                        onChange={() => field.onChange('read_only')}
                      />
                      <span className="font-medium">
                        {t('integrations:api_integration.sheet.create_dialog.field.scope_read')}
                      </span>
                      <p className="text-xs text-foreground-subtle mt-1">
                        {t(
                          'integrations:api_integration.sheet.create_dialog.field.scope_read_description',
                        )}
                      </p>
                    </label>
                    <label
                      className={`flex-1 cursor-pointer rounded-lg border p-3 text-sm transition-colors ${
                        field.value === 'read_write'
                          ? 'border-foreground bg-muted'
                          : 'border-border hover:border-border'
                      }`}
                      data-testid="scope-option-read-write"
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        value="read_write"
                        checked={field.value === 'read_write'}
                        onChange={() => field.onChange('read_write')}
                      />
                      <span className="font-medium">
                        {t('integrations:api_integration.sheet.create_dialog.field.scope_write')}
                      </span>
                      <p className="text-xs text-foreground-subtle mt-1">
                        {t(
                          'integrations:api_integration.sheet.create_dialog.field.scope_write_description',
                        )}
                      </p>
                    </label>
                  </div>
                )}
              />
            </FormItem>

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={isCreating}
                data-testid="cancel-create-token-button"
              >
                {t('integrations:api_integration.sheet.create_dialog.button.cancel')}
              </Button>
              <Button
                type="submit"
                variant="dark"
                disabled={isCreating || !isValid}
                data-testid="submit-create-token-button"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="animate-spin size-4" />
                    {t('integrations:api_integration.sheet.create_dialog.button.creating')}
                  </>
                ) : (
                  <>
                    <Check className="size-4" />
                    {t('integrations:api_integration.sheet.create_dialog.button.create')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
